import React from 'react';
import { C } from '../constants';
import {
  requisitosDeFactura, emitirFacturaPedido, notaCreditoPedido, notaDebitoPedido,
  reciboCajaPedido, anularDocumentoSiigo, debitarPedido, listarMisPedidosRemotos,
  visitasSinTecnico, asignarTecnicoVisita, anularVisita, cobrarRecurrentes,
} from '../services/pagos';

/**
 * PEDIDO → DOCUMENTOS. La cara de admin de todo lo que Siigo puede hacer sobre una venta.
 *
 * 🔴 POR QUÉ EXISTE ESTA PANTALLA, y no solo los endpoints: en este repo ya pasó tres veces que
 * una capacidad quedara construida y **sin nadie que la llame** — `supplier-profile` devolvía 404
 * y la pestaña Empresa decía «guardado» sin guardar nada; `list-quotes` nunca funcionó desde el
 * cliente; los cuatro endpoints del portal de proveedores eran inalcanzables. Un endpoint sin
 * consumidor no es una feature a medias: es una feature que **nadie sabe que no existe**.
 *
 * Todo lo de acá corre normalmente SOLO (la cola automática lo hace). Esto es el tablero para
 * cuando algo se rindió y hay que destrabarlo a mano, con el motivo real a la vista.
 */

const CAJA = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px' };
const TITULO = { fontSize: 11, fontWeight: 800, color: C.heading, textTransform: 'uppercase', letterSpacing: 0.8, margin: '0 0 8px' };
const CAMPO = {
  background: 'transparent', color: 'inherit', border: `1px solid ${C.border}`,
  borderRadius: 6, padding: '7px 10px', fontSize: 12.5,
};
const fmtCOP = (n) => `$${Math.round(Number(n) || 0).toLocaleString('es-CO')}`;

function Boton({ onClick, children, tono = 'suave', disabled }) {
  const fuerte = tono === 'fuerte';
  const peligro = tono === 'peligro';
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      style={{
        background: fuerte ? C.teal : 'transparent',
        color: fuerte ? C.heading : (peligro ? C.orange : C.muted),
        border: `1px solid ${fuerte ? C.teal : (peligro ? `${C.orange}66` : C.border)}`,
        borderRadius: 7, padding: '6px 12px', fontSize: 11.5,
        fontWeight: fuerte ? 800 : 600, cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}>
      {children}
    </button>
  );
}

export default function PedidoFiscal() {
  const [correo, setCorreo] = React.useState('');
  const [pedidos, setPedidos] = React.useState([]);
  const [sel, setSel] = React.useState(null);
  const [requisitos, setRequisitos] = React.useState(null);
  const [msg, setMsg] = React.useState('');
  const [visitas, setVisitas] = React.useState([]);
  const [tecnico, setTecnico] = React.useState('');
  // 🪤 El motivo NO es decoración: es lo que queda escrito en la nota crédito, y es
  //    lo que hace que anular sea un acto deliberado y no un clic de más.
  const [motivo, setMotivo] = React.useState('');

  const buscar = async () => {
    setMsg('Buscando…');
    const r = await listarMisPedidosRemotos(correo.trim().toLowerCase()).catch((e) => ({ ok: false, error: e.message }));
    if (!r?.ok) { setMsg(r?.error || 'No se pudo buscar.'); return; }
    setPedidos(r.pedidos || []);
    setMsg((r.pedidos || []).length ? '' : 'Ese correo no tiene pedidos.');
  };

  const abrir = async (p) => {
    setSel(p);
    setRequisitos(null);
    const r = await requisitosDeFactura(p.id).catch(() => null);
    if (r?.ok) setRequisitos(r);
  };

  const correr = async (fn, etiqueta) => {
    if (!sel) return;
    setMsg(`${etiqueta}…`);
    const r = await fn(sel.id).catch((e) => ({ ok: false, error: e.message }));
    setMsg(r?.ok
      ? `${etiqueta}: ${r.numero || r.invoice_id || 'listo'}`
      : (r?.detalle || r?.error || 'Siigo respondió con un error.'));
  };

  const cargarVisitas = async () => {
    const r = await visitasSinTecnico().catch((e) => ({ ok: false, error: e.message }));
    if (r?.ok) { setVisitas(r.visitas || []); setMsg(''); } else setMsg(r?.error || 'No se pudo leer la cola.');
  };

  const asignar = async (id) => {
    if (!tecnico.trim()) { setMsg('Escribe el correo del técnico que hizo la visita.'); return; }
    const r = await asignarTecnicoVisita(id, { tech_email: tecnico.trim() }).catch((e) => ({ ok: false, error: e.message }));
    setMsg(r?.ok ? 'Técnico asignado: el giro sale solo en el siguiente ciclo.' : (r?.detalle || r?.error || 'No se pudo asignar.'));
    cargarVisitas();
  };

  /**
   * ANULAR la visita y emitir su nota crédito.
   *
   * 🪤 El servidor responde 409 `no_anulable` cuando esa plata ya se acreditó o se liquidó, y su
   * `detalle` dice a dónde ir a corregirla. Se muestra tal cual: un «no se pudo» genérico manda a
   * buscar el error donde no está.
   */
  const anular = async (id) => {
    if (!motivo.trim()) { setMsg('Escribe por qué se anula: eso queda en la nota crédito.'); return; }
    const r = await anularVisita(id, { motivo: motivo.trim() }).catch((e) => ({ ok: false, error: e.message }));
    setMsg(r?.ok
      ? `Visita #${id} anulada${r.nota_credito?.numero ? ` · nota crédito ${r.nota_credito.numero}` : ''}.`
      : (r?.detalle || r?.error || 'No se pudo anular.'));
    if (r?.ok) setMotivo('');
    cargarVisitas();
  };

  React.useEffect(() => { cargarVisitas(); }, []);

  const puedeFacturar = requisitos?.puede_facturar;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={CAJA}>
        <h3 style={TITULO}>Pedido → documentos</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input value={correo} onChange={(e) => setCorreo(e.target.value)} placeholder="Correo del comprador"
            style={{ ...CAMPO, minWidth: 240, flex: 1 }} />
          <Boton tono="fuerte" onClick={buscar}>Buscar pedidos</Boton>
        </div>

        {pedidos.length > 0 && (
          <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
            {pedidos.slice(0, 12).map((p) => (
              <button key={p.id} type="button" onClick={() => abrir(p)}
                style={{
                  textAlign: 'left', background: sel?.id === p.id ? `${C.teal}14` : 'transparent',
                  border: `1px solid ${sel?.id === p.id ? C.teal : C.border}`, borderRadius: 8,
                  padding: '8px 10px', cursor: 'pointer', color: 'inherit',
                }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.heading }}>
                  {p.id} · {fmtCOP(p.total)} · {p.estado}
                  {p.tipo === 'visita' && <span style={{ color: C.yellow }}> · visita técnica</span>}
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                  {p.siigo_numero
                    ? <>Factura {p.siigo_numero}{p.siigo_cufe ? ' · CUFE ✓' : ''}</>
                    : 'Sin factura todavía'}
                  {p.recaudo_motivo ? ` · ${p.recaudo_motivo}` : ''}
                </div>
              </button>
            ))}
          </div>
        )}

        {sel && (
          <div style={{ marginTop: 12, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
            {/* Qué le falta al cliente ANTES de emitir: una FV mal formada quema consecutivo DIAN. */}
            {requisitos && !puedeFacturar && (
              <div style={{ fontSize: 11.5, color: C.orange, marginBottom: 8, lineHeight: 1.5 }}>
                No se puede facturar todavía: falta <strong>{(requisitos.faltan || []).join(', ')}</strong>.
                Pedirle el dato al cliente cuesta un mensaje; emitir mal cuesta una nota crédito.
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Boton tono="fuerte" disabled={requisitos ? !puedeFacturar : false}
                onClick={() => correr(emitirFacturaPedido, 'Factura emitida')}>
                Emitir factura DIAN
              </Boton>
              <Boton onClick={() => correr(reciboCajaPedido, 'Recibo de caja')}>Recibo de caja</Boton>
              <Boton onClick={() => correr(notaCreditoPedido, 'Nota crédito')}>Nota crédito</Boton>
              <Boton onClick={() => correr(notaDebitoPedido, 'Nota débito')}>Nota débito</Boton>
              <Boton onClick={() => correr((id) => debitarPedido({ pedido_id: id }), 'Débito al medio anclado')}>
                Debitar medio anclado
              </Boton>
              {sel.siigo_invoice_id && (
                <Boton tono="peligro"
                  onClick={() => correr(() => anularDocumentoSiigo(sel.siigo_invoice_id), 'Anulación')}>
                  Anular documento
                </Boton>
              )}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
              {/* Decirlo acá evita el error caro: la FV estampada no se anula, se corrige. */}
              ⚠️ Una factura ya estampada ante la DIAN <strong>no se anula</strong>: se corrige con
              <strong> nota crédito</strong>. «Anular» sirve para documentos internos o una FV que nunca
              llegó a la DIAN.
            </div>
          </div>
        )}
        {msg && <div style={{ fontSize: 11.5, color: C.orange, marginTop: 10 }}>{msg}</div>}
      </div>

      <div style={{ ...CAJA, borderLeft: `3px solid ${visitas.length ? C.orange : C.teal}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h3 style={{ ...TITULO, margin: 0 }}>Visitas pagadas sin técnico</h3>
          <Boton onClick={() => cobrarRecurrentes()}>Forzar cobro recurrente del mes</Boton>
        </div>
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
          {/* Mientras estén acá, esa plata no se le puede girar a nadie. */}
          El cliente ya pagó la visita y nadie registró quién fue al sitio. Hasta que se asigne, el
          giro del 80% <strong>no puede salir</strong> — y tampoco se descuenta de la obra si el
          negocio se cierra.
        </div>
        {visitas.length === 0 ? (
          <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>Ninguna pendiente.</div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
              <input value={tecnico} onChange={(e) => setTecnico(e.target.value)}
                placeholder="Correo del técnico que hizo la visita"
                style={{ ...CAMPO, flex: 1, minWidth: 220 }} />
              {/* 🔴 Anular era la mitad que faltaba: se podía cobrar una visita y NO devolverla.
                  El motivo va a la nota crédito, así que se pide antes del botón, no después. */}
              <input value={motivo} onChange={(e) => setMotivo(e.target.value)}
                placeholder="…o el motivo, si se va a anular"
                style={{ ...CAMPO, flex: 1, minWidth: 220 }} />
            </div>
            <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
              {visitas.map((v) => (
                <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12, color: C.text }}>
                  <span>#{v.id} · {fmtCOP(v.valor_cop)} · {v.cliente_email || 'sin correo'}{v.quote_id ? ` · cot. ${v.quote_id}` : ''}</span>
                  <span style={{ display: 'flex', gap: 6 }}>
                    <Boton tono="fuerte" onClick={() => asignar(v.id)}>Asignar</Boton>
                    <Boton tono="peligro" onClick={() => anular(v.id)}>Anular</Boton>
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
